import { useState, useEffect } from 'react';
import { ContentImageField } from '@/components/admin/content-image-field';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { Content } from '@/types/custom';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { ContentKeywordManager } from '@/components/admin/content-keyword-manager';
import { ScopePicker } from '@/components/admin/scope-picker';
import { AudiencePicker } from '@/components/admin/audience-picker';
import { Switch } from '@/components/ui/switch';
import { Trash2 } from 'lucide-react';
import { useProfile } from '@/lib/providers';
import { AdminRole } from '@/types/custom';

interface ArticleEditProps {
  content?: Content;
  onSave: () => void;
  isOpen: boolean;
  handleClose: () => void;
}

/**
 * Dialog for editing or creating an article.
 */
export default function ArticleEdit({
  content: prevContent,
  onSave,
  isOpen,
  handleClose,
}: ArticleEditProps) {
  const { profile } = useProfile();
  const isAdmin = profile?.role === AdminRole;

  const [title, setTitle] = useState(prevContent?.title || '');
  const [description, setDescription] = useState(
    prevContent?.description || '',
  );
  const [body, setBody] = useState(prevContent?.body || '');
  const [imageUrl, setImageUrl] = useState(prevContent?.image_url || '');
  const [published, setPublished] = useState(prevContent?.published ?? true);
  const [scopeType, setScopeType] = useState(prevContent?.scope_type || 'global');
  const [scopeId, setScopeId] = useState<string | null>(prevContent?.scope_id || null);
  const [roleScope, setRoleScope] = useState<string | null>(prevContent?.role_scope ?? null);
  const [hnhScopeType, setHnhScopeType] = useState(prevContent?.hnh_scope_type || 'global');
  const [hnhScopeId, setHnhScopeId] = useState<string | null>(prevContent?.hnh_scope_id || null);

  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    setTitle(prevContent?.title || '');
    setDescription(prevContent?.description || '');
    setBody(prevContent?.body || '');
    setImageUrl(prevContent?.image_url || '');
    setPublished(prevContent?.published ?? true);
    setScopeType(prevContent?.scope_type || 'global');
    setScopeId(prevContent?.scope_id || null);
    setRoleScope(prevContent?.role_scope ?? null);
    setHnhScopeType(prevContent?.hnh_scope_type || 'global');
    setHnhScopeId(prevContent?.hnh_scope_id || null);
  }, [prevContent]);

  const handleEdit = async () => {
    if (!prevContent) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('content')
        .update({
          title,
          description,
          body,
          image_url: imageUrl,
          published,
          role_scope: roleScope,
          scope_type: scopeType,
          scope_id: scopeType === 'global' ? null : scopeId,
          hnh_scope_type: hnhScopeType,
          hnh_scope_id: hnhScopeType === 'global' || hnhScopeType === 'hoofdhartuser' ? null : hnhScopeId,
        })
        .eq('id', prevContent.id);

      if (error) throw error;

      toast({
        title: 'Artikel bijgewerkt',
        description: `${title} is bijgewerkt.`,
      });

      onSave();
      handleClose();
    } catch (error: any) {
      toast({
        title: 'Fout bij opslaan',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreate = async () => {
    setIsSaving(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const { error } = await supabase.from('content').insert({
        title,
        description,
        body,
        image_url: imageUrl,
        content_type: 'article',
        published,
        role_scope: roleScope,
        scope_type: scopeType,
        scope_id: scopeType === 'global' ? null : scopeId,
        hnh_scope_type: hnhScopeType,
        hnh_scope_id: hnhScopeType === 'global' || hnhScopeType === 'hoofdhartuser' ? null : hnhScopeId,
      });

      if (error) throw error;

      toast({
        title: 'Artikel toegevoegd',
        description: `${title} is toegevoegd.`,
      });

      onSave();
      handleClose();
    } catch (error: any) {
      toast({
        title: 'Fout bij toevoegen',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!prevContent) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('content')
        .delete()
        .eq('id', prevContent.id);

      if (error) throw error;

      toast({
        title: 'Artikel verwijderd',
        description: `${prevContent.title} is verwijderd.`,
      });

      onSave();
      handleClose();
    } catch (error: any) {
      toast({
        title: 'Fout bij verwijderen',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const pageTitle = prevContent ? 'Artikel Bewerken' : 'Artikel Toevoegen';
  const editMode = !!prevContent;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => (open ? null : handleClose())}
    >
      <DialogContent
        className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto"
        aria-label={pageTitle}
      >
        <DialogHeader>
          <DialogTitle>{pageTitle}</DialogTitle>
          <DialogDescription className="sr-only">{pageTitle}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-2">
          <div>
            <Label htmlFor="title">Titel</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="description">Korte Beschrijving</Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="body">Uitgebreide Informatie</Label>
            <RichTextEditor
              content={body}
              onChange={setBody}
              placeholder="Begin met typen..."
              className="min-h-[200px]"
            />
          </div>

          <ContentImageField
            value={imageUrl}
            onChange={setImageUrl}
            label="Afbeelding"
            required
          />

          {isAdmin && (
            <AudiencePicker
              roleScope={roleScope}
              scopeType={scopeType}
              scopeId={scopeId}
              hnhScopeType={hnhScopeType}
              hnhScopeId={hnhScopeId}
              onRoleScopeChange={setRoleScope}
              onScopeChange={(type, id) => {
                setScopeType(type);
                setScopeId(id);
              }}
              onHnhScopeChange={(type, id) => {
                setHnhScopeType(type);
                setHnhScopeId(id);
              }}
            />
          )}

          <div className="flex items-center justify-between">
            <Label htmlFor="published">Gepubliceerd</Label>
            <Switch
              id="published"
              checked={published}
              onCheckedChange={setPublished}
              className="data-[state=checked]:bg-green-500"
            />
          </div>

          {/* for now  we don't care to link keywords to the articles (but we could) */}
          {/* {prevContent && (
            <div className="space-y-1">
              <ContentKeywordManager contentId={prevContent.id} />
            </div>
          )} */}
        </div>

        <DialogFooter>
          {editMode && (
            <AlertDialog
              open={showDeleteDialog}
              onOpenChange={setShowDeleteDialog}
            >
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  disabled={isSaving}
                  className="mr-auto"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Verwijderen
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Artikel verwijderen?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Weet u zeker dat u dit artikel wilt verwijderen? Deze actie
                    kan niet ongedaan worden gemaakt.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuleren</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete}>
                    Verwijderen
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <Button variant="outline" onClick={() => handleClose()}>
            Annuleren
          </Button>
          <Button
            onClick={editMode ? handleEdit : handleCreate}
            disabled={isSaving || !title || !description || !imageUrl}
          >
            {isSaving ? 'Opslaan...' : editMode ? 'Bijwerken' : 'Opslaan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
